/**
 * Settings → Ónettengt: device storage (usage / quota, persist() status), the "Tilbúið án nets"
 * check-list, saved packs (size, status, remove / re-download / retry) and the recent-songs cache.
 */
import {
  CheckCircle2,
  Circle,
  Clock3,
  Folder,
  HardDrive,
  HardDriveDownload,
  ListMusic,
  Music,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  WifiOff,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { serverNow } from '../../api/client';
import { useToast } from '../../components/toast';
import { Button, cx } from '../../components/ui';
import { useI18n } from '../../i18n';
import { formatRelativeTime } from '../../lib/dates';
import { formatSize, packTitle } from '../../offline/format';
import { canSaveOffline, useOfflineSnapshot } from '../../offline/hooks';
import { InstallForOfflineBanner } from '../../offline/InstallForOfflineBanner';
import { clearRecent, syncManifest } from '../../offline/manager';
import { checkReadiness, isReady, type Readiness } from '../../offline/readiness';
import { PackProgress, SavePackSheet } from '../../offline/SavePackSheet';
import type { PackView } from '../../offline/store';
import { SettingsSection } from './parts';

interface Estimate {
  usage: number;
  quota: number;
  persisted: boolean;
}

/** Kept for compatibility with earlier imports; same as offline/format formatSize. */
export const formatBytes = formatSize;

function useStorageEstimate(deps: unknown): [Estimate | null | undefined, () => void] {
  const [est, setEst] = useState<Estimate | null | undefined>(undefined);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    const s = navigator.storage;
    if (!s?.estimate) {
      setEst(null);
      return;
    }
    void Promise.all([s.estimate(), s.persisted ? s.persisted().catch(() => false) : Promise.resolve(false)])
      .then(([e, persisted]) => {
        if (alive) setEst(e.usage != null && e.quota ? { usage: e.usage, quota: e.quota, persisted } : null);
      })
      .catch(() => alive && setEst(null));
    return () => {
      alive = false;
    };
  }, [deps, tick]);
  return [est, useCallback(() => setTick((n) => n + 1), [])];
}

function useReadiness(packReady: boolean): Readiness | null {
  const [r, setR] = useState<Readiness | null>(null);
  useEffect(() => {
    let alive = true;
    void checkReadiness(packReady).then((x) => alive && setR(x));
    return () => {
      alive = false;
    };
  }, [packReady]);
  return r;
}

const KIND_ICON: Record<PackView['row']['kind'], LucideIcon> = { song: Music, collection: ListMusic, all: HardDriveDownload };

function Check({ ok, label }: { ok: boolean; label: string }) {
  const Icon = ok ? CheckCircle2 : Circle;
  return (
    <li className={cx('flex items-center gap-2.5', ok ? 'text-[var(--ink)]' : 'text-[var(--muted)]')}>
      <Icon className={cx('size-5 shrink-0', ok ? 'text-[var(--ok-text)]' : 'text-[var(--muted)]')} aria-hidden />
      <span>{label}</span>
    </li>
  );
}

function PackRowItem({ view }: { view: PackView }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const Icon = view.row.kind === 'collection' && view.songIds.length === 0 ? Folder : KIND_ICON[view.row.kind];
  return (
    <li>
      <button type="button" onClick={() => setOpen(true)} className="flex w-full items-start gap-4 px-5 py-4 text-left transition hover:bg-[var(--surface-2)] active:bg-[var(--accent-soft)]">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-[var(--accent-soft)] text-[var(--accent)]">
          <Icon className="size-5" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold break-words">{packTitle(t, view.row)}</span>
          {view.row.kind !== 'song' ? <span className="block text-[15px] text-[var(--muted)]">{t('offline.songs', { count: view.songIds.length })}</span> : null}
          <span className="mt-2 block">
            <PackProgress view={view} />
          </span>
        </span>
      </button>
      <SavePackSheet open={open} onClose={() => setOpen(false)} kind={view.row.kind} refId={view.row.refId} title={view.row.title} />
    </li>
  );
}

export function StorageSection() {
  const { t, loc } = useI18n();
  const toast = useToast();
  const snap = useOfflineSnapshot();
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const packBytes = snap.packs.reduce((n, p) => n + p.bytesDone, 0);
  const [est, refreshEstimate] = useStorageEstimate(packBytes + snap.recent.bytes);
  const packReady = snap.packs.some((p) => p.status === 'complete');
  const ready = useReadiness(packReady);
  const [busy, setBusy] = useState<'persist' | 'sync' | 'recent' | null>(null);

  // Opened from the downloads bar (/settings#settings-offline): bring this section into view.
  useEffect(() => {
    if (location.hash === '#settings-offline') ref.current?.scrollIntoView({ block: 'start' });
  }, [location.hash]);

  const pct = est ? Math.min(100, Math.max(est.usage > 0 ? 1 : 0, Math.round((est.usage / est.quota) * 100))) : 0;

  const persist = async () => {
    setBusy('persist');
    try {
      await navigator.storage?.persist?.();
    } catch {
      /* not supported */
    }
    refreshEstimate();
    setBusy(null);
  };

  const sync = async () => {
    setBusy('sync');
    const ok = await syncManifest();
    if (!ok) toast.show(t('offline.storage.sync_failed'), { tone: 'error' });
    setBusy(null);
  };

  const emptyRecent = async () => {
    setBusy('recent');
    await clearRecent().catch(() => undefined);
    refreshEstimate();
    setBusy(null);
  };

  return (
    <div ref={ref} className="scroll-mt-24">
      <SettingsSection icon={WifiOff} title={t('settings.offline')} id="settings-offline">
        <div className="divide-y divide-[var(--line)]">
          {/* Device storage */}
          <div className="flex flex-col gap-3 p-5">
            <div className="flex items-center gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-[var(--accent-soft)] text-[var(--accent)]">
                <HardDrive className="size-5" aria-hidden />
              </div>
              <div className="font-semibold">{t('settings.storage')}</div>
            </div>
            {est === undefined ? null : est ? (
              <>
                <div
                  className="h-3 w-full overflow-hidden rounded-full bg-[var(--surface-2)] ring-1 ring-[var(--line)]"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={pct}
                  aria-label={t('settings.storage')}
                >
                  <div className="h-full rounded-full bg-[var(--accent)]" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-[var(--ink-2)]">{t('settings.storage_usage', { used: formatSize(est.usage, loc), quota: formatSize(est.quota, loc) })}</p>
                {est.persisted ? (
                  <p className="flex items-center gap-2 text-[15px] text-[var(--ok-text)]">
                    <ShieldCheck className="size-4 shrink-0" aria-hidden />
                    {t('offline.storage.persisted')}
                  </p>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="flex flex-1 items-center gap-2 text-[15px] text-[var(--muted)]">
                      <ShieldAlert className="size-4 shrink-0" aria-hidden />
                      {t('offline.storage.not_persisted')}
                    </p>
                    {typeof navigator.storage?.persist === 'function' ? (
                      <Button variant="secondary" icon={ShieldCheck} busy={busy === 'persist'} onClick={() => void persist()}>
                        {t('offline.storage.persist')}
                      </Button>
                    ) : null}
                  </div>
                )}
              </>
            ) : (
              <p className="text-[var(--muted)]">{t('settings.storage_unknown')}</p>
            )}
          </div>

          {/* Ready offline */}
          {ready ? (
            <div className="flex flex-col gap-3 p-5">
              <div className={cx('flex items-center gap-2 text-[18px] font-bold', isReady(ready) ? 'text-[var(--ok-text)]' : 'text-[var(--ink-2)]')}>
                {isReady(ready) ? <CheckCircle2 className="size-6" aria-hidden /> : <Clock3 className="size-6" aria-hidden />}
                {t(isReady(ready) ? 'offline.storage.ready' : 'offline.storage.not_ready')}
              </div>
              <ul className="flex flex-col gap-1.5 text-[16px]">
                <Check ok={ready.swActive} label={t('offline.storage.check_sw')} />
                <Check ok={ready.shellCached} label={t('offline.storage.check_shell')} />
                <Check ok={ready.corePersisted} label={t('offline.storage.check_core')} />
                <Check ok={ready.packReady} label={t('offline.storage.check_pack')} />
              </ul>
            </div>
          ) : null}

          {!canSaveOffline() ? (
            <div className="p-5">
              <InstallForOfflineBanner />
            </div>
          ) : null}

          {/* Packs */}
          <div className="flex flex-col">
            <div className="flex flex-wrap items-center gap-3 px-5 pt-5 pb-2">
              <h3 className="flex-1 font-semibold">{t('offline.storage.packs')}</h3>
              <Button variant="ghost" icon={RefreshCw} busy={busy === 'sync' || snap.syncing} onClick={() => void sync()}>
                {t('offline.storage.sync_now')}
              </Button>
            </div>
            {snap.manifestAt ? (
              <p className="px-5 pb-2 text-[14px] text-[var(--muted)]">
                {snap.syncFailed ? t('offline.storage.sync_failed') : t('offline.storage.synced', { time: formatRelativeTime(snap.manifestAt, serverNow(), loc) })}
              </p>
            ) : null}
            {snap.packs.length === 0 ? (
              <p className="px-5 pb-5 text-[var(--muted)]">{t('offline.storage.no_packs')}</p>
            ) : (
              <ul className="divide-y divide-[var(--line)] border-t border-[var(--line)]">
                {snap.packs.map((p) => (
                  <PackRowItem key={p.row.id} view={p} />
                ))}
              </ul>
            )}
          </div>

          {/* Recent songs (automatic) */}
          <div className="flex flex-wrap items-center gap-3 p-5">
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{t('offline.storage.recent')}</div>
              <p className="text-[15px] text-[var(--muted)]">
                {snap.recent.songs > 0
                  ? t('offline.storage.recent_desc', { count: snap.recent.songs, size: formatSize(snap.recent.bytes, loc) })
                  : t('offline.storage.recent_empty')}
              </p>
            </div>
            {snap.recent.songs > 0 ? (
              <Button variant="secondary" busy={busy === 'recent'} onClick={() => void emptyRecent()}>
                {t('offline.storage.clear_recent')}
              </Button>
            ) : null}
          </div>
        </div>
      </SettingsSection>
    </div>
  );
}
